import { useCallback, useMemo, useState } from "react";
import { type ExactShiftFilterType, ShiftFilterType } from "../../components/staffing/MonthlyView/utils";
import { DEFAULT_POPULAR_SHIFT_TIMES_AMOUNT, getPopularShiftTimes } from "../../components/UIToolkit/molecules/TimeFilterChips";
import { useAuth } from "../../contexts/AuthProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { localStorageService } from "../../services/LocalStorageService";
import type { StoredShiftWindow } from "../../utils/models";

/** Each Schedule Template layout remembers its own filters. */
export type ScheduleTemplateFilterTab = "weekly" | "consecutive" | "coverage";

type FilterScope = { id: string; filterKey: string; departmentId: string; userId: string };

const SHIFT_WINDOW_FORMAT = "HH:mm";

/**
 * A filter value read from storage for the current user and department. Re-read in the render either changes —
 * the page doesn't remount on a department switch. `scope` is null until both are known, so nothing is read or
 * written under a half-built key.
 */
const useScopedFilterState = <V>(filterKey: string, read: (scope: FilterScope | null) => V) => {
	const { userInfo } = useAuth();
	const { chosenDepartment } = useHospitalData();
	const userId = userInfo?.id;
	const departmentId = chosenDepartment.department_id;
	const scope = useMemo<FilterScope | null>(
		() => (userId && departmentId ? { id: `${userId}_${departmentId}_${filterKey}`, filterKey, departmentId, userId } : null),
		[userId, departmentId, filterKey],
	);
	const scopeId = scope ? scope.id : null;
	const [state, setState] = useState(() => ({ scopeId, value: read(scope) }));
	const current = state.scopeId === scopeId ? state : { scopeId, value: read(scope) };
	if (current !== state) {
		// React's "adjusting state when a prop changes": re-renders straight away with the new scope's value.
		setState(current);
	}
	const setValue = useCallback((value: V) => setState({ scopeId, value }), [scopeId]);
	return { scope, value: current.value, setValue };
};

type ExclusionState = { excluded: string[]; touchedThisVisit: boolean };

/**
 * A multi-select filter remembered per user, department and tab. What's stored is what the user turned OFF, and the
 * selection is derived from `options` on every render: an option that appears later (a new location, one that only
 * arrives with the shifts fetch) shows selected, and a turned-off option that no longer exists is ignored.
 */
export const usePersistedExclusionFilter = <T extends string>(tab: ScheduleTemplateFilterTab, filterName: string, options: T[]) => {
	const { scope, value, setValue } = useScopedFilterState<ExclusionState>(`${tab}_${filterName}`, (readScope) => ({
		// DEFAULT: nothing stored means nothing turned off — every option shows, as before filters were remembered.
		excluded: (readScope && localStorageService.getScheduleTemplateExcludedOptions(readScope.filterKey, readScope.departmentId, readScope.userId)) ?? [],
		touchedThisVisit: false,
	}));

	const selected = useMemo(() => {
		const remaining = options.filter((option) => !value.excluded.includes(option));
		// DEFAULT: a saved choice that turns off every option on offer wasn't made against this list — show them all
		// rather than an empty grid nobody chose. Turning everything off by hand during this visit still empties it.
		return remaining.length === 0 && !value.touchedThisVisit ? options : remaining;
	}, [options, value]);

	const setSelected = useCallback(
		(next: T[]) => {
			// Turned-off options that aren't on offer right now (another rotation, still loading) stay off.
			const offScreen = value.excluded.filter((name) => !options.some((option) => option === name));
			const excluded = [...options.filter((option) => !next.includes(option)), ...offScreen];
			setValue({ excluded, touchedThisVisit: true });
			if (scope) localStorageService.setScheduleTemplateExcludedOptions(scope.filterKey, scope.departmentId, excluded, scope.userId);
		},
		[options, value.excluded, setValue, scope],
	);

	return [selected, setSelected] as const;
};

/**
 * `usePersistedExclusionFilter` for a filter whose empty selection means "no constraint" (the Coverage tab's
 * dropdowns): every option selected reads as `[]`, and clearing the selection selects everything.
 */
export const usePersistedEmptyMeansAllFilter = <T extends string>(tab: ScheduleTemplateFilterTab, filterName: string, options: T[]) => {
	const [selected, setSelected] = usePersistedExclusionFilter(tab, filterName, options);
	const value = useMemo<T[]>(() => (selected.length === options.length ? [] : selected), [selected, options.length]);
	const onChange = useCallback((next: T[]) => setSelected(next.length === 0 ? options : next), [setSelected, options]);
	return [value, onChange] as const;
};

const isSameWindow = (offered: ExactShiftFilterType, stored: StoredShiftWindow) =>
	offered.start.toFormat(SHIFT_WINDOW_FORMAT) === stored.start && offered.end.toFormat(SHIFT_WINDOW_FORMAT) === stored.end;

/**
 * The time-filter chip, remembered per user, department and tab. Matched against the chips `TimeFilterChips` offers
 * for the same shifts, so a restored window always lights its chip.
 */
export const usePersistedShiftFilter = (tab: ScheduleTemplateFilterTab, allShiftsTimes: ExactShiftFilterType[]) => {
	const { scope, value, setValue } = useScopedFilterState<StoredShiftWindow | null>(`${tab}_time`, (readScope) =>
		readScope ? localStorageService.getScheduleTemplateShiftWindow(readScope.filterKey, readScope.departmentId, readScope.userId) : null,
	);
	const offeredShiftWindows = useMemo(() => getPopularShiftTimes(allShiftsTimes, DEFAULT_POPULAR_SHIFT_TIMES_AMOUNT), [allShiftsTimes]);

	const selectedShiftFilter = useMemo<ShiftFilterType | ExactShiftFilterType>(() => {
		if (value === null) return ShiftFilterType.All;
		// DEFAULT: a remembered time with no chip on offer (shift times changed, shifts still loading) shows All — a
		// filter with no lit chip would hide shifts for no visible reason. It stays saved and applies once the chip is back.
		return offeredShiftWindows.find((offered) => isSameWindow(offered, value)) ?? ShiftFilterType.All;
	}, [offeredShiftWindows, value]);

	const handleShiftFilterChange = useCallback(
		(filter: ShiftFilterType | ExactShiftFilterType, selected: boolean) => {
			if (!selected) return;
			// These tabs offer only All and exact windows; All is stored as nothing.
			const window = typeof filter === "string" ? null : { start: filter.start.toFormat(SHIFT_WINDOW_FORMAT), end: filter.end.toFormat(SHIFT_WINDOW_FORMAT) };
			setValue(window);
			if (scope) localStorageService.setScheduleTemplateShiftWindow(scope.filterKey, scope.departmentId, window, scope.userId);
		},
		[setValue, scope],
	);

	return { selectedShiftFilter, handleShiftFilterChange, offeredShiftWindows };
};
